import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Trophy, Medal, Award, Loader2, ArrowRight, Gift } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { LeaderboardEntry } from '@/types';

// Prize values in Shekels
const prizeValues: Record<number, number> = {
  1: 1000,
  2: 800,
  3: 700,
  4: 600,
  5: 500,
  6: 400,
  7: 300,
  8: 200,
};

export default function Leaderboard() {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await api.getLeaderboard();
      if (response.success && response.data) {
        setLeaderboard(response.data);
      }
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        if (rank <= 8) {
          return <Gift className="w-5 h-5 text-emerald-400" />;
        }
        return <span className="text-gray-400 font-bold">{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/30';
      default:
        return 'bg-gray-900/50 border-gray-800';
    }
  };

  const getPrize = (rank: number): number | null => {
    return prizeValues[rank] || null;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Moon className="w-8 h-8 text-gold-500" />
            <span className="text-xl font-amiri font-bold text-gold-500">رحلة عبر الزمن</span>
          </Link>

          {isAuthenticated ? (
            <Link to="/competition" className="btn-secondary text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              العودة للمسابقة
            </Link>
          ) : (
            <Link to="/login" className="btn-primary text-sm">
              تسجيل الدخول
            </Link>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <Trophy className="w-16 h-16 text-gold-500 mx-auto mb-4" />
          <h1 className="text-3xl font-amiri font-bold text-gold-500 mb-2">
            لوحة المتصدرين
          </h1>
          <p className="text-gray-400">
            أفضل المتسابقين في رحلة عبر الزمن
          </p>
        </div>

        {/* Prize Summary */}
        <div className="card border border-gold-500/30 bg-gold-500/5 mb-8">
          <div className="flex flex-wrap items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold-500" />
              <span className="text-cream">مجموع الجوائز:</span>
              <span className="text-gold-500 font-bold text-xl">4,500</span>
              <span className="text-gray-400">شيكل</span>
            </div>
            <span className="text-gray-600">|</span>
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" />
              <span className="text-cream">8 فائزين</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-gray-400">لا توجد نتائج بعد</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Top 3 */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {leaderboard.slice(0, 3).map((entry) => {
                const prize = getPrize(entry.rank);
                return (
                  <div
                    key={entry.rank}
                    className={`card border ${getRankBg(entry.rank)} text-center ${
                      entry.rank === 1 ? 'md:order-2 md:scale-105' : entry.rank === 2 ? 'md:order-1' : 'md:order-3'
                    }`}
                  >
                    <div className="mb-2">{getRankIcon(entry.rank)}</div>
                    <h3 className="text-lg font-bold text-cream mb-1">{entry.full_name}</h3>
                    <p className="text-gold-500 font-bold text-2xl">{entry.total_score}</p>
                    <p className="text-sm text-gray-400 mb-2">نقطة</p>
                    {prize && (
                      <div className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
                        <Gift className="w-4 h-4 text-emerald-400" />
                        <span className="text-emerald-400 font-bold">{prize}</span>
                        <span className="text-emerald-400/70 text-sm">شيكل</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Rest of leaderboard */}
            {leaderboard.slice(3).map((entry) => {
              const prize = getPrize(entry.rank);
              return (
                <div
                  key={entry.rank}
                  className={`card border flex items-center gap-4 ${
                    user?.full_name === entry.full_name
                      ? 'border-gold-500/50 bg-gold-500/5'
                      : prize
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-gray-800'
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-cream">{entry.full_name}</h3>
                    <p className="text-sm text-gray-400">المحطة {entry.current_station}</p>
                  </div>
                  <div className="text-left flex items-center gap-4">
                    {prize && (
                      <div className="flex items-center gap-1 text-emerald-400">
                        <Gift className="w-4 h-4" />
                        <span className="font-bold">{prize}</span>
                        <span className="text-xs">ش</span>
                      </div>
                    )}
                    <div>
                      <p className="text-gold-500 font-bold text-xl">{entry.total_score}</p>
                      <p className="text-xs text-gray-400">نقطة</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Prize Table */}
        <div className="card mt-12">
          <h3 className="text-xl font-bold text-gold-500 mb-4 text-center">جدول الجوائز</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(prizeValues).map(([rank, amount]) => (
              <div key={rank} className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-gray-400 text-sm">المركز {
                  rank === '1' ? 'الأول' :
                  rank === '2' ? 'الثاني' :
                  rank === '3' ? 'الثالث' :
                  rank === '4' ? 'الرابع' :
                  rank === '5' ? 'الخامس' :
                  rank === '6' ? 'السادس' :
                  rank === '7' ? 'السابع' : 'الثامن'
                }</p>
                <p className="text-cream font-bold text-lg">{amount} <span className="text-sm text-gray-400">شيكل</span></p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            برعاية شباب مسجد السلام - عبلين
          </p>
        </div>
      </footer>
    </div>
  );
}
